// @flow
import { delay, getFormikFormInputsFromColConfigAndInputSource } from '@src/common-utils/utils';
import { CLUSTER_DETAILS_CONFIG } from '@src/configuration/cluster-details';
import {
  CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG,
  IS_RTCE_LIVE_IN_CE1,
} from '@src/constants';
import { getTableflowVersion } from '@src/custom-hooks/useTableflowVersion';
import {
  getTableflowInputsFromCluster,
  TABLEFLOW_WIRE_KEY_BY_VERSION,
} from '@components/cluster/cluster-monthly-inputs/tableflow-config';

import { addMonthIndexToInputs, DELAY_BETWEEN_MUTATIONS_MS, throwOnError } from './utils';

const BASELINE_CLUSTER_FIELDS = new Set([
  'name',
  'cluster_type',
  'provider',
  'region',
  'az_configuration',
  'networking_type',
  'sla',
  'partition_count',
  'existing_storage',
  'is_follower_fetch_enabled',
  'retention_days',
  'throughput',
]);

const OPTIONAL_CLUSTER_FIELDS = [
  CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  ...(IS_RTCE_LIVE_IN_CE1 ? [CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG] : []),
];

// Adds month indexes and builds the monthly input structures shared by both
// the validation reshape and the BE update payload.
// Tableflow's source key varies by version too (a v2 cluster carries tableflow_v2, not
// tableflow) -- getTableflowInputsFromCluster resolves that instead of assuming one key.
export const buildMonthlyInputsFromCluster = (cluster, tableflowVersion) => {
  const { throughput, ksqldb, cluster_linking: clusterLinking, connectors, rtce } = cluster;
  const tableflow = getTableflowInputsFromCluster(cluster, tableflowVersion);
  const tableflowKey = TABLEFLOW_WIRE_KEY_BY_VERSION[tableflowVersion];

  return {
    throughput: { inputs: addMonthIndexToInputs(throughput.inputs) },
    ...(ksqldb && {
      ksqldb: { inputs: addMonthIndexToInputs(ksqldb.inputs) },
    }),
    ...(clusterLinking && {
      cluster_linking: { inputs: addMonthIndexToInputs(clusterLinking.inputs) },
    }),
    ...(Object.keys(tableflow).length > 0 && {
      [tableflowKey]: {
        storage_type: tableflow.storage_type,
        inputs: addMonthIndexToInputs(tableflow.inputs),
      },
    }),
    ...(IS_RTCE_LIVE_IN_CE1 &&
      rtce && {
        rtce: {
          storage_type: rtce.storage_type,
          inputs: addMonthIndexToInputs(rtce.inputs),
        },
      }),
    ...(connectors &&
      connectors.length > 0 && {
        connectors: connectors.map((conn) => ({
          ...conn,
          inputs: addMonthIndexToInputs(conn.inputs),
        })),
      }),
  };
};

// Reshapes a baseline cluster object into the UI form structure
// so that CLUSTER_DETAILS_CONFIG validations (which use from[3].value etc.) work correctly
// Reshapes baseline cluster data into the structure that getFormikFormInputsFromColConfigAndInputSource
// expects as inputSource. Flattens retention and adds month indexes.
// The inputTransformationFuncs in CLUSTER_DETAILS_CONFIG handle the rest.
const reshapeClusterForValidation = (cluster, tableflowVersion) => {
  const { retention, ...rest } = cluster;
  const monthly = buildMonthlyInputsFromCluster(cluster, tableflowVersion);

  return {
    ...rest,
    retention_days: retention?.retention_days,
    is_finite_retention: retention?.is_finite_retention,
    ...monthly,
  };
};

export const validateClusters = async (clusters, estimate, metaData) => {
  const tableflowVersion = getTableflowVersion(estimate);

  for (let i = 0; i < clusters.length; i++) {
    const cluster = clusters[i];
    const reshaped = reshapeClusterForValidation(cluster, tableflowVersion);

    const fieldsToValidate = new Set(BASELINE_CLUSTER_FIELDS);
    OPTIONAL_CLUSTER_FIELDS.forEach((field) => {
      if (cluster[field]) {
        fieldsToValidate.add(field);
      }
    });

    const applicableConfig = CLUSTER_DETAILS_CONFIG.filter((col) =>
      fieldsToValidate.has(col.backendFieldName)
    );

    const { initialValues, validationSchema } = getFormikFormInputsFromColConfigAndInputSource(
      applicableConfig,
      reshaped,
      { estimate, metaData }
    );

    try {
      await validationSchema.validate(initialValues, { abortEarly: true, strict: true });
    } catch (err) {
      err.path = `clusters[${i}] "${cluster.name}" → ${err.path}`;
      throw err;
    }
  }
};

export const createClustersFromBaseline = async (
  clusters,
  estimateId,
  currentVersion,
  addCluster,
  updateCluster,
  estimate
) => {
  let version = currentVersion;
  const tableflowVersion = getTableflowVersion(estimate);

  for (const cluster of clusters) {
    const { error: createError, data: createData } = await addCluster({
      estimateId,
      payload: {
        name: cluster.name,
        estimate_version: version,
      },
    });

    throwOnError(createError, `creating cluster "${cluster.name}"`);

    const clusterId = createData.id;
    version = createData.estimate_version;

    const monthly = buildMonthlyInputsFromCluster(cluster, tableflowVersion);

    const updatePayload = {
      id: clusterId,
      estimate_id: estimateId,
      estimate_version: version,
      name: cluster.name,
      inputs: {
        cluster_type: cluster.cluster_type,
        provider: cluster.provider,
        region: cluster.region,
        az_configuration: cluster.az_configuration,
        networking_type: cluster.networking_type,
        sla: cluster.sla,
        partition_count: cluster.partition_count,
        existing_storage: cluster.existing_storage,
        is_follower_fetch_enabled: cluster.is_follower_fetch_enabled,
        retention: cluster.retention,
      },
      ...monthly,
    };

    const { error: updateError, data: updateData } = await updateCluster({
      estimateId,
      clusterId,
      payload: updatePayload,
    });

    throwOnError(
      updateError,
      `updating cluster "${cluster.name}" with inputs:\n\n${JSON.stringify(updatePayload, null, 2)}`
    );

    version = updateData.estimate_version;
    await delay(DELAY_BETWEEN_MUTATIONS_MS);
  }

  return version;
};
